import { useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'

import { api, unwrap } from '../api/client'
import type { Board, ModuleNode } from '../api/types'
import { t } from '../i18n'
import { ErrorNotice, Notice, Pending } from '../ui'
import { BoardRules } from './BoardRules'

/**
 * The leaderboard, per project, inside one owner.
 *
 * Three product rules are load-bearing in what this renders and what it does
 * not. A board never crosses an owner boundary, so there is no scope wider
 * than the project on offer here. A row shows a name, a crew and an employer
 * and no photograph, which is what the trailer television shows too. And the
 * board is configurable and published: what it counts is a project setting and
 * it is visible on the board rather than buried in an administrator's screen.
 *
 * Shadow mode is surfaced rather than hidden. A board that is counting but not
 * publishing looks identical to an empty board unless it says which it is, and
 * "why am I not on the board" is the first question a crew asks.
 */
export function BoardCard({
  projectId,
  modules = [],
}: {
  projectId: string
  modules?: readonly ModuleNode[]
}) {
  const [window_, setWindow] = useState<'week' | 'month'>('week')
  const [showRules, setShowRules] = useState(false)
  const closeRules = useCallback(() => setShowRules(false), [])

  const board = useQuery({
    queryKey: ['engage', 'board', projectId, window_],
    queryFn: async (): Promise<Board> =>
      unwrap(
        await api.GET('/engage/leaderboard', {
          params: { query: { project_id: projectId, window: window_ } },
        }),
      ),
  })

  const mine = useQuery({
    queryKey: ['engage', 'points', projectId, window_],
    queryFn: async () =>
      unwrap(
        await api.GET('/engage/points/me', {
          params: { query: { project_id: projectId, window: window_ } },
        }),
      ),
  })

  return (
    <section className="card" aria-labelledby="board-heading">
      <div className="row">
        <h3 id="board-heading" style={{ flex: 1 }}>
          {t('engage.board.title')}
        </h3>
        <label className="visually-hidden" htmlFor="board-window">
          {t('engage.board.title')}
        </label>
        <select
          id="board-window"
          value={window_}
          onChange={(event) => setWindow(event.target.value === 'month' ? 'month' : 'week')}
        >
          <option value="week">{t('engage.board.window.week')}</option>
          <option value="month">{t('engage.board.window.month')}</option>
        </select>
      </div>

      {board.isPending ? <Pending /> : null}
      {board.error ? <ErrorNotice error={board.error} retry={() => void board.refetch()} /> : null}
      {board.data?.shadow ? <Notice tone="warn">{t('engage.board.shadow')}</Notice> : null}

      {mine.data ? (
        <p className="muted">
          {t('engage.points.mine')}: <strong>{mine.data.total}</strong>{' '}
          <span className="mono">({mine.data.window_key})</span>
        </p>
      ) : null}

      {board.data && board.data.rows.length === 0 ? (
        <p className="muted">{t('engage.board.empty')}</p>
      ) : null}

      <ol className="board">
        {board.data?.rows.map((row) => (
          <li key={row.person_id} data-you={row.is_you}>
            <span className="rank">{row.rank}</span>
            <span>
              {row.display_name}
              {row.is_you ? ` · ${t('engage.board.you')}` : ''}
              <span className="crew">
                {[row.crew, row.company_name].filter(Boolean).join(' · ') || '—'}
              </span>
            </span>
            <span className="score">{row.points}</span>
          </li>
        ))}
      </ol>

      {board.data ? (
        <p className="muted">
          {t('engage.board.rules_before')}
          <button type="button" className="text-link" onClick={() => setShowRules(true)}>
            {t('engage.board.rules_link')}
          </button>
          {t('engage.board.rules_after')}
        </p>
      ) : null}
      {showRules ? (
        <BoardRules projectId={projectId} modules={modules} onClose={closeRules} />
      ) : null}
      <p className="muted">
        <Link to={`/p/${projectId}/board/display`}>{t('engage.display.open')}</Link>
      </p>
    </section>
  )
}
